import { useEffect, useMemo, useState } from 'react';
import {
  BookOpen,
  Scale,
  TrendingUp,
  Users,
  Truck,
  AlertCircle,
  ArrowDownToLine,
} from 'lucide-react';
import { Badge, Button, Card, CardBody, CardHeader, CardTitle, Input } from '@solara/ui-shared';
import { useTranslation } from '@solara/i18n';
import { accountingApi, ApiError } from '@solara/api-client';
import type {
  BalanceSheetDto,
  ProfitLossDto,
  CustomerLedgerRowDto,
  SupplierLedgerRowDto,
  LedgerGroupDto,
} from '@solara/api-client';

type Tab = 'balance' | 'pnl' | 'ar' | 'ap';

function aed(minor: string | number | bigint): string {
  const n = typeof minor === 'bigint' ? Number(minor) : Number(minor);
  return new Intl.NumberFormat('en-AE', {
    style: 'currency',
    currency: 'AED',
    maximumFractionDigits: 2,
  }).format(n / 100);
}

export default function AccountingPage() {
  const { t } = useTranslation('p1');
  const [tab, setTab] = useState<Tab>('balance');
  const [error, setError] = useState<string | null>(null);

  const [bs, setBs] = useState<BalanceSheetDto | null>(null);
  const [pnl, setPnl] = useState<ProfitLossDto | null>(null);
  const [ar, setAr] = useState<CustomerLedgerRowDto[]>([]);
  const [ap, setAp] = useState<SupplierLedgerRowDto[]>([]);

  const [asOf, setAsOf] = useState<string>(new Date().toISOString().slice(0, 10));
  const [periodStart, setPeriodStart] = useState<string>(`${new Date().getFullYear()}-01-01`);
  const [periodEnd, setPeriodEnd] = useState<string>(new Date().toISOString().slice(0, 10));

  useEffect(() => {
    void loadFor(tab);
  }, [tab]);

  async function loadFor(t0: Tab) {
    setError(null);
    try {
      if (t0 === 'balance') setBs(await accountingApi.balanceSheet(asOf));
      else if (t0 === 'pnl') setPnl(await accountingApi.profitLoss({ periodStart, periodEnd }));
      else if (t0 === 'ar') setAr(await accountingApi.customerLedger());
      else if (t0 === 'ap') setAp(await accountingApi.supplierLedger());
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.loadFailed'));
    }
  }

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">
          {t('accounting.title', { defaultValue: 'Bookkeeping' })}
        </h1>
        <p className="text-fg-muted text-sm">
          {t('accounting.subtitle', {
            defaultValue: 'Balance sheet, profit & loss, customer and supplier sub-ledgers.',
          })}
        </p>
      </header>

      {error && (
        <Card className="border-danger/40 bg-danger/5 rounded-2xl">
          <CardBody className="flex items-center gap-2 text-sm text-danger">
            <AlertCircle className="h-4 w-4" />
            <span>{error}</span>
          </CardBody>
        </Card>
      )}

      <div className="flex flex-wrap gap-1 rounded-2xl border border-border bg-surface p-1 text-sm">
        <TabBtn active={tab === 'balance'} onClick={() => setTab('balance')} icon={<Scale className="h-3.5 w-3.5" />}>
          {t('accounting.balanceSheet', { defaultValue: 'Balance sheet' })}
        </TabBtn>
        <TabBtn active={tab === 'pnl'} onClick={() => setTab('pnl')} icon={<TrendingUp className="h-3.5 w-3.5" />}>
          {t('accounting.profitLoss', { defaultValue: 'P&L' })}
        </TabBtn>
        <TabBtn active={tab === 'ar'} onClick={() => setTab('ar')} icon={<Users className="h-3.5 w-3.5" />}>
          {t('accounting.customerLedger', { defaultValue: 'Customer ledger' })}
        </TabBtn>
        <TabBtn active={tab === 'ap'} onClick={() => setTab('ap')} icon={<Truck className="h-3.5 w-3.5" />}>
          {t('accounting.supplierLedger', { defaultValue: 'Supplier ledger' })}
        </TabBtn>
      </div>

      {tab === 'balance' && (
        <div className="space-y-4">
          <div className="flex items-end gap-3 flex-wrap">
            <Input
              label={t('accounting.asOf', { defaultValue: 'As of' })}
              type="date"
              value={asOf}
              onChange={(e) => setAsOf(e.target.value)}
              className="max-w-xs"
            />
            <Button variant="secondary" onClick={() => void loadFor('balance')}>
              {t('common.refresh', { defaultValue: 'Refresh' })}
            </Button>
          </div>
          {bs && <BalanceSheetView bs={bs} t={t} />}
        </div>
      )}

      {tab === 'pnl' && (
        <div className="space-y-4">
          <div className="flex items-end gap-3 flex-wrap">
            <Input
              label={t('accounting.periodStart', { defaultValue: 'From' })}
              type="date"
              value={periodStart}
              onChange={(e) => setPeriodStart(e.target.value)}
              className="max-w-xs"
            />
            <Input
              label={t('accounting.periodEnd', { defaultValue: 'To' })}
              type="date"
              value={periodEnd}
              onChange={(e) => setPeriodEnd(e.target.value)}
              className="max-w-xs"
            />
            <Button variant="secondary" onClick={() => void loadFor('pnl')}>
              {t('common.refresh', { defaultValue: 'Refresh' })}
            </Button>
          </div>
          {pnl && <ProfitLossView pnl={pnl} t={t} />}
        </div>
      )}

      {tab === 'ar' && <CustomerLedgerView rows={ar} t={t} />}
      {tab === 'ap' && <SupplierLedgerView rows={ap} t={t} />}
    </div>
  );
}

function TabBtn({
  active,
  onClick,
  icon,
  children,
}: {
  active: boolean;
  onClick: () => void;
  icon?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-2 rounded-xl px-3 py-1.5 text-sm font-medium transition ${
        active ? 'bg-primary text-primary-fg shadow' : 'text-fg-muted hover:bg-surface-muted'
      }`}
    >
      {icon}
      <span>{children}</span>
    </button>
  );
}

function LedgerGroupSection({
  title,
  group,
  tone,
}: {
  title: string;
  group: LedgerGroupDto;
  tone: 'primary' | 'success' | 'warning';
}) {
  if (group.rows.length === 0)
    return (
      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle>{title}</CardTitle>
        </CardHeader>
        <CardBody>
          <p className="py-3 text-center text-sm text-fg-muted">{aed(0)}</p>
        </CardBody>
      </Card>
    );
  return (
    <Card className="rounded-2xl">
      <CardHeader className="flex items-center justify-between">
        <CardTitle>{title}</CardTitle>
        <Badge tone={tone === 'success' ? 'success' : tone === 'warning' ? 'warning' : 'info'} className="text-[10px]">
          {aed(group.totalMinor)}
        </Badge>
      </CardHeader>
      <CardBody>
        <ul className="divide-y divide-border text-sm">
          {group.rows.map((row) => (
            <li key={row.code} className="flex items-center justify-between py-2">
              <span>
                <span className="font-mono text-xs text-fg-muted me-2">{row.code}</span>
                {row.name}
              </span>
              <span className="font-medium">{aed(row.balanceMinor)}</span>
            </li>
          ))}
        </ul>
      </CardBody>
    </Card>
  );
}

function BalanceSheetView({ bs, t }: { bs: BalanceSheetDto; t: (k: string, o?: any) => string }) {
  const totalAssets = BigInt(bs.assets.totalMinor);
  const totalLiabEq = BigInt(bs.liabilities.totalMinor) + BigInt(bs.equity.totalMinor);
  const balanced = totalAssets === totalLiabEq;
  return (
    <div className="space-y-4">
      <Card className="rounded-2xl">
        <CardBody className="flex items-center justify-between text-sm">
          <div>
            <p className="text-xs text-fg-muted">{t('accounting.asOf', { defaultValue: 'As of' })}</p>
            <p className="font-medium">{bs.asOf}</p>
          </div>
          <Badge tone={balanced ? 'success' : 'warning'} className="text-[10px]">
            {balanced
              ? t('accounting.balanced', { defaultValue: 'Balanced' })
              : t('accounting.outOfBalance', { defaultValue: 'Out of balance' })}
          </Badge>
        </CardBody>
      </Card>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <LedgerGroupSection
          title={t('accounting.assets', { defaultValue: 'Assets' })}
          group={bs.assets}
          tone="primary"
        />
        <LedgerGroupSection
          title={t('accounting.liabilities', { defaultValue: 'Liabilities' })}
          group={bs.liabilities}
          tone="warning"
        />
        <LedgerGroupSection
          title={t('accounting.equity', { defaultValue: 'Equity' })}
          group={bs.equity}
          tone="success"
        />
      </div>
    </div>
  );
}

function ProfitLossView({ pnl, t }: { pnl: ProfitLossDto; t: (k: string, o?: any) => string }) {
  return (
    <div className="space-y-4">
      <Card className="rounded-2xl">
        <CardBody className="flex items-center justify-between text-sm">
          <div>
            <p className="text-xs text-fg-muted">
              {t('accounting.period', { defaultValue: 'Period' })}
            </p>
            <p className="font-medium">
              {pnl.periodStart} → {pnl.periodEnd}
            </p>
          </div>
          <Badge tone={BigInt(pnl.netIncomeMinor) >= 0n ? 'success' : 'danger'} className="text-[10px]">
            {t('accounting.netIncome', { defaultValue: 'Net income' })}: {aed(pnl.netIncomeMinor)}
          </Badge>
        </CardBody>
      </Card>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <LedgerGroupSection
          title={t('accounting.income', { defaultValue: 'Income' })}
          group={pnl.income}
          tone="success"
        />
        <LedgerGroupSection
          title={t('accounting.expenses', { defaultValue: 'Expenses' })}
          group={pnl.expense}
          tone="warning"
        />
      </div>
    </div>
  );
}

function CustomerLedgerView({
  rows,
  t,
}: {
  rows: CustomerLedgerRowDto[];
  t: (k: string, o?: any) => string;
}) {
  const total = useMemo(() => rows.reduce((s, r) => s + BigInt(r.openMinor), 0n), [rows]);
  return (
    <Card className="rounded-2xl">
      <CardHeader className="flex items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <BookOpen className="h-4 w-4" />
          {t('accounting.customerLedger', { defaultValue: 'Customer ledger' })}
        </CardTitle>
        <Badge tone="warning" className="text-[10px]">
          {t('accounting.totalOpen', { defaultValue: 'Total open' })}: {aed(total)}
        </Badge>
      </CardHeader>
      <CardBody>
        {rows.length === 0 ? (
          <p className="py-6 text-center text-sm text-fg-muted">
            {t('accounting.noCustomers', { defaultValue: 'No customer invoices yet.' })}
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs text-fg-muted">
              <tr className="text-start">
                <th className="py-1 text-start">{t('accounting.buyer', { defaultValue: 'Customer' })}</th>
                <th className="py-1 text-end">{t('accounting.invoiceCount', { defaultValue: '#' })}</th>
                <th className="py-1 text-end">{t('accounting.billed', { defaultValue: 'Billed' })}</th>
                <th className="py-1 text-end">{t('accounting.open', { defaultValue: 'Open' })}</th>
                <th className="py-1 text-end">{t('accounting.oldestDays', { defaultValue: 'Oldest (d)' })}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.buyerTrn + row.buyerName} className="border-t border-border">
                  <td className="py-2">
                    <p className="font-medium">{row.buyerName}</p>
                    {row.buyerTrn && <p className="font-mono text-xs text-fg-muted">{row.buyerTrn}</p>}
                  </td>
                  <td className="py-2 text-end">{row.invoiceCount}</td>
                  <td className="py-2 text-end">{aed(row.billedMinor)}</td>
                  <td className="py-2 text-end font-medium">{aed(row.openMinor)}</td>
                  <td className="py-2 text-end">{row.daysOldest ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardBody>
    </Card>
  );
}

function SupplierLedgerView({
  rows,
  t,
}: {
  rows: SupplierLedgerRowDto[];
  t: (k: string, o?: any) => string;
}) {
  const total = useMemo(() => rows.reduce((s, r) => s + BigInt(r.openMinor), 0n), [rows]);
  return (
    <Card className="rounded-2xl">
      <CardHeader className="flex items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <ArrowDownToLine className="h-4 w-4" />
          {t('accounting.supplierLedger', { defaultValue: 'Supplier ledger' })}
        </CardTitle>
        <Badge tone="warning" className="text-[10px]">
          {t('accounting.totalOpen', { defaultValue: 'Total open' })}: {aed(total)}
        </Badge>
      </CardHeader>
      <CardBody>
        {rows.length === 0 ? (
          <p className="py-6 text-center text-sm text-fg-muted">
            {t('accounting.noSuppliers', { defaultValue: 'No supplier invoices yet.' })}
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs text-fg-muted">
              <tr>
                <th className="py-1 text-start">{t('accounting.seller', { defaultValue: 'Supplier' })}</th>
                <th className="py-1 text-end">{t('accounting.invoiceCount', { defaultValue: '#' })}</th>
                <th className="py-1 text-end">{t('accounting.billed', { defaultValue: 'Billed' })}</th>
                <th className="py-1 text-end">{t('accounting.open', { defaultValue: 'Open' })}</th>
                <th className="py-1 text-end">{t('accounting.oldestDays', { defaultValue: 'Oldest (d)' })}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.sellerTrn + row.sellerName} className="border-t border-border">
                  <td className="py-2">
                    <p className="font-medium">{row.sellerName}</p>
                    {row.sellerTrn && <p className="font-mono text-xs text-fg-muted">{row.sellerTrn}</p>}
                  </td>
                  <td className="py-2 text-end">{row.invoiceCount}</td>
                  <td className="py-2 text-end">{aed(row.billedMinor)}</td>
                  <td className="py-2 text-end font-medium">{aed(row.openMinor)}</td>
                  <td className="py-2 text-end">{row.daysOldest ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardBody>
    </Card>
  );
}
